import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Description from "../../components/description/Description";
import GameSetting from "../../components/gamesetting/GameSetting";
import TopBar from "../../components/topbar/TopBar";
import Highscore from "../../components/highscore/Highscore";
import { HowToPlay } from "../../components/howtoplaymodal/HowToPlay";
import { Button, Container, Page } from "../../global/styles/styles";
import { ThemeContext, UserContext } from "../../global";

const SinglePlayer = ({ isMobile, isMobileSafari }) => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { theme, setTheme } = useContext(ThemeContext);
  const gameMode = JSON.parse(localStorage.getItem("hsMode")) || {};
  const [gameSettings, setGameSettings] = useState({
    title: "Easy",
    difficulty: gameMode.difficulty || "e",
    time: gameMode.time || 60,
    mode: gameMode.mode || "p",
  });
  const [highscore, setHighscore] = useState();
  const hsString = `hs${gameSettings.difficulty + gameSettings.time}`;
  const hsMode = {
    string: hsString,
    difficulty: gameSettings.difficulty,
    time: gameSettings.time,
    mode: gameSettings.mode,
  };

  function handleClick() {
    try {
      if (isMobile) {
        document.body.requestFullscreen();
      } else if (isMobileSafari) {
        Element.webkitRequestFullscreen();
      }
    } catch {
    } finally {
      localStorage.setItem("hsMode", JSON.stringify(hsMode));
      navigate("/game", { state: { gameSettings } });
    }
  }

  useEffect(() => {
    setHighscore(user?.highscores?.[gameSettings.mode]?.[hsMode.string] || 0);
    return () => {};
  }, [gameSettings]);

  useEffect(() => {
    switch (gameSettings.difficulty) {
      case "e":
        setGameSettings((prevState) => ({
          ...prevState,
          time: 60,
          title: "Easy",
        }));
        break;
      case "m":
        setGameSettings((prevState) => ({
          ...prevState,
          time: 90,
          title: "Normal",
        }));
        break;
      case "h":
        setGameSettings((prevState) => ({
          ...prevState,
          time: 120,
          title: "Hard",
        }));
        break;
    }
    return () => {};
  }, [gameSettings.difficulty]);

  const difficultySetting = {
    title: "Difficulty",
    setting: "difficulty",
    buttons: [
      {
        text: "Easy",
        value: "e",
      },
      {
        text: "Normal",
        value: "m",
      },
      {
        text: "Hard",
        value: "h",
      },
    ],
  };

  const modeSetting = {
    title: "Game Mode",
    setting: "mode",
    buttons: [
      {
        text: "Practice",
        value: "p",
      },
      {
        text: "Time Trial",
        value: "timeTrial",
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Single Player | CompuTiles</title>
        <meta
          name="description"
          content="Play CompuTiles in single-player mode. Sharpen your mental math and problem-solving skills with endless challenges."
        />
        <link rel="canonical" href="https://www.computiles.com/singleplayer" />

        {/* Open Graph */}
        <meta property="og:title" content="Single Player | CompuTiles" />
        <meta
          property="og:description"
          content="Play CompuTiles in single-player mode. Sharpen your mental math and problem-solving skills with endless challenges."
        />
        <meta
          property="og:image"
          content="https://www.computiles.com/numero_icon-transformed.png"
        />
        <meta
          property="og:url"
          content="https://www.computiles.com/singleplayer"
        />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="Single Player | CompuTiles" />
        <meta
          name="twitter:description"
          content="Sharpen your mental math with solo puzzles in CompuTiles."
        />
        <meta
          name="twitter:image"
          content="https://www.computiles.com/numero_icon-transformed.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Page>
        {theme.tutorial && (
          <HowToPlay theme={theme} setTheme={setTheme} onClick={() => {}} />
        )}
        <TopBar title={"Single Player"} navigateTo={"/"} />
        <Container $flex={1}></Container>
        <Container $flex={3}>
          <GameSetting
            setGameSettings={setGameSettings}
            $active={gameSettings.mode}
            settings={modeSetting}
          />
        </Container>
        <Container $flex={3}>
          <GameSetting
            setGameSettings={setGameSettings}
            $active={gameSettings.difficulty}
            settings={difficultySetting}
          />
        </Container>
        <Container $flex={6} $flexDirection={"column"}>
          {gameSettings.mode === "timeTrial" && (
            <Highscore
              highscore={user?.highscores[gameSettings.mode][hsString]}
            />
          )}
          <Description gameSettings={gameSettings} highscore={highscore} />
        </Container>
        <Container $flex={4} $width={"70%"}>
          <Button $flex={1} onClick={handleClick}>
            Play
          </Button>
        </Container>
      </Page>
    </>
  );
};

export default SinglePlayer;
